"use client";

import { type FC, type ReactNode, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  NavigationActionInterceptorContext,
  type NavigationActionInterceptor,
  type NavigationActionInterceptorState,
} from "../../contexts/NavigationActionInterceptorContext";
import { useDebug } from "../../hooks/useDebug";
import { Modal } from "../Modal";
import { Body } from "../Body";
import { Button } from "../Button";
import { Flex } from "../Flex";

type NavigationActionInterceptorProviderProps = {
  children: ReactNode;
};

/**
 * Provides navigation action interception.
 * Components can register interceptors that block navigation and trigger
 * a confirmation modal before proceeding.
 */
export const NavigationActionInterceptorProvider: FC<
  NavigationActionInterceptorProviderProps
> = ({ children }) => {
  const { t } = useTranslation();
  const { log } = useDebug();

  const interceptorsRef = useRef<Set<NavigationActionInterceptor>>(new Set());
  const pendingNavigationRef = useRef<(() => void) | null>(null);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);

  const registerInterceptor: NavigationActionInterceptorState["registerInterceptor"] =
    (interceptor) => {
      interceptorsRef.current.add(interceptor);
      log(
        "red",
        "NavigationActionInterceptorProvider",
        `Interceptor registered (total: ${interceptorsRef.current.size})`,
      );
      return () => {
        interceptorsRef.current.delete(interceptor);
        log(
          "red",
          "NavigationActionInterceptorProvider",
          `Interceptor unregistered (total: ${interceptorsRef.current.size})`,
        );
      };
    };

  const requestNavigation: NavigationActionInterceptorState["requestNavigation"] =
    (callback) => {
      log(
        "red",
        "NavigationActionInterceptorProvider",
        `requestNavigation called, ${interceptorsRef.current.size} interceptor(s)`,
      );

      const isBlocked = Array.from(interceptorsRef.current).some(
        (interceptor) => interceptor(),
      );

      log(
        "red",
        "NavigationActionInterceptorProvider",
        isBlocked ? "Navigation BLOCKED — showing modal" : "Navigation ALLOWED",
      );

      if (isBlocked) {
        pendingNavigationRef.current = callback;
        setIsConfirmationVisible(true);
      } else {
        callback();
      }
    };

  const handleStay = () => {
    log("red", "NavigationActionInterceptorProvider", "User chose STAY");
    pendingNavigationRef.current = null;
    setIsConfirmationVisible(false);
  };

  const handleLeave = () => {
    log("red", "NavigationActionInterceptorProvider", "User chose LEAVE");
    const navigate = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    setIsConfirmationVisible(false);
    navigate?.();
  };

  const contextValue: NavigationActionInterceptorState = {
    registerInterceptor,
    requestNavigation,
  };

  return (
    <NavigationActionInterceptorContext.Provider value={contextValue}>
      {children}
      {isConfirmationVisible && (
        <Modal
          placement="center"
          size="small"
          title={t("formNavigateAway.title")}
        >
          <Body>{t("formNavigateAway.description")}</Body>
          <Flex justify="space-between" align="flex-end" gap="s">
            <Button variant="background" border="default" onClick={handleStay}>
              {t("formNavigateAway.stay")}
            </Button>
            <Button variant="error" onClick={handleLeave}>
              {t("formNavigateAway.leave")}
            </Button>
          </Flex>
        </Modal>
      )}
    </NavigationActionInterceptorContext.Provider>
  );
};
